import React, { useCallback } from 'react';
import { chakra, Text } from '@chakra-ui/react';
import {
  getContributionIPI,
  getContributionIPN,
  getContributionISNI,
  getLegalName,
  getSeparator,
} from 'app/components/Releases/VersionCreditsList/utils';
import { getAvatarImage, trimText } from 'app/utils/utils';
import { GroupedVersionCredits } from 'types/releases';
import { ListSubheader } from '../../ChakraTableList';
import { ChakraAvatar } from '../../Core';

interface CreditListSubheaderProps {
  credit: GroupedVersionCredits;
}

const TextLabel = chakra(Text, {
  baseStyle: {
    fontWeight: 600,
    fontSize: 'xs',
    textTransform: 'none',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
});

export const CreditListSubheader: React.FC<CreditListSubheaderProps> = ({
  credit,
}) => {
  const { contributor } = credit.contribution.snapshot || {};
  const ipi = getContributionIPI(credit);
  const ipn = getContributionIPN(credit);
  const isni = getContributionISNI(credit);
  const legalName = getLegalName(credit, true);
  const hasName = legalName || contributor.profileCard?.displayName;

  const generateTooltip = useCallback((id: string, type: string) => {
    switch (type) {
      case 'IPI':
        return `Interested Parties Information (IPI): ${id}`;
      case 'IPN':
        return `International Performer Number (IPN): ${id}`;
      case 'ISNI':
        return `International Standard Name Identifier (ISNI): ${id}`;
      default:
        return id;
    }
  }, []);

  return (
    <ListSubheader w="fit-content">
      <ChakraAvatar
        mx={1}
        size="xxs"
        initials={contributor.profileCard?.initials}
        colors={contributor.profileCard?.avatarColors}
        src={getAvatarImage(contributor.profileCard?.profileImages)}
      />
      <TextLabel
        data-testid="display-name"
        title={contributor.profileCard?.displayName ?? '-'}
      >
        {trimText(contributor.profileCard?.displayName || '', 35)}
      </TextLabel>
      {legalName ? getSeparator('-') : ''}
      <TextLabel
        color="textSubdued"
        data-testid="legal-name"
        title={getLegalName(credit, false)}
      >
        {trimText(legalName, 50)}
      </TextLabel>
      {hasName && ipi ? getSeparator() : ''}
      {ipi && (
        <TextLabel
          color="textSubdued"
          data-testid="contributor-ipi"
          title={generateTooltip(ipi, 'IPI')}
        >
          {ipi}
        </TextLabel>
      )}
      {hasName && ipn ? getSeparator() : ''}
      {ipn && (
        <TextLabel
          color="textSubdued"
          data-testid="contributor-ipn"
          title={generateTooltip(ipn, 'IPN')}
        >
          {ipn}
        </TextLabel>
      )}
      {hasName && isni ? getSeparator() : ''}
      {isni && (
        <TextLabel
          color="textSubdued"
          data-testid="contributor-isni"
          title={generateTooltip(isni, 'ISNI')}
        >
          {isni}
        </TextLabel>
      )}
    </ListSubheader>
  );
};
